import { stationAbbr, stationLabel } from "../demand/demandMath.ts";
import type { SimResult } from "./types.ts";

/**
 * The arithmetic behind the time–distance diagram, kept free of React so it
 * can be tested on its own (timeDistanceMath.test.ts).
 */

/**
 * Where each stop sits on the distance axis, as a share of 0–1 from the first
 * stop to the last.
 *
 * Stops are spaced by scheduled run time rather than evenly: a trip then
 * draws as a straight line, and a bend in it would mean something. The run
 * time of each link is the median over every trip that runs it, either way,
 * so one odd row cannot skew the axis. A result with no trips, or a link no
 * trip covers, falls back to equal spacing.
 */
export function stopPositions(result: SimResult): number[] {
  const n = result.input.stops.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  const samples: number[][] = Array.from({ length: n - 1 }, () => []);
  for (const t of result.trips) {
    const calls = t.trip.calls;
    for (let i = 0; i < calls.length - 1; i++) {
      const a = calls[i];
      const b = calls[i + 1];
      const link = Math.min(a.stop, b.stop);
      if (Math.abs(a.stop - b.stop) !== 1 || link < 0 || link >= n - 1) continue;
      samples[link].push(Math.abs(b.timeMin - a.timeMin));
    }
  }

  const runs = samples.map((s) => (s.length ? median(s) : 0));
  const usable = runs.every((r) => r > 0);
  const cumulative = [0];
  for (let k = 0; k < n - 1; k++) cumulative.push(cumulative[k] + (usable ? runs[k] : 1));
  const total = cumulative[n - 1];
  return cumulative.map((c) => (total > 0 ? c / total : 0));
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** The whole hours that cover the simulated day. */
export function defaultWindow(result: SimResult): [number, number] {
  const from = Math.floor(result.totals.firstMin / 60) * 60;
  let to = Math.ceil(result.totals.lastMin / 60) * 60;
  if (to <= from) to = from + 60;
  return [from, to];
}

/**
 * Hour marks for the time axis. Every hour when there is room for the label,
 * otherwise every second or third, always on multiples of the step.
 */
export function hourTicks(fromMin: number, toMin: number, pxPerHour: number): number[] {
  const step = pxPerHour >= 44 ? 60 : pxPerHour >= 24 ? 120 : 180;
  const ticks: number[] = [];
  for (let t = Math.ceil(fromMin / step) * step; t <= toMin; t += step) ticks.push(t);
  return ticks;
}

/**
 * A stop name that fits `maxChars`: the full label when it does, the
 * initials when it does not, and a clipped label when even those do not.
 */
export function fitLabel(name: string, maxChars: number): string {
  const label = stationLabel(name);
  if (label.length <= maxChars) return label;
  const abbr = stationAbbr(name);
  if (abbr.length <= maxChars) return abbr;
  return maxChars > 1 ? `${label.slice(0, Math.max(1, maxChars - 1))}…` : label.slice(0, 1);
}

/** One drawn stretch of a trip, in pixels, with a way back to the trip. */
export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  trip: number;
  link: number;
}

/** Distance from a point to a segment, in the same units. */
export function distanceToSegment(px: number, py: number, s: Segment): number {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - s.x1) * dx + (py - s.y1) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = s.x1 + t * dx;
  const cy = s.y1 + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** The segment under the pointer, if one is within `maxDist` pixels. */
export function nearestSegment(segments: Segment[], px: number, py: number, maxDist: number): Segment | null {
  let best: Segment | null = null;
  let bestDist = maxDist;
  for (const s of segments) {
    // Cheap rejection first: the pointer has to be alongside the stretch.
    if (px < Math.min(s.x1, s.x2) - maxDist || px > Math.max(s.x1, s.x2) + maxDist) continue;
    if (py < Math.min(s.y1, s.y2) - maxDist || py > Math.max(s.y1, s.y2) + maxDist) continue;
    const d = distanceToSegment(px, py, s);
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best;
}

/** Riders left behind by a trip, grouped by stop, busiest stop first. */
export function leftBehindByStop(calls: Array<{ stop: number; leftBehind: number }>): Array<{ stop: number; riders: number }> {
  const byStop = new Map<number, number>();
  for (const c of calls) {
    if (c.leftBehind >= 0.5) byStop.set(c.stop, (byStop.get(c.stop) ?? 0) + c.leftBehind);
  }
  return [...byStop.entries()].map(([stop, riders]) => ({ stop, riders })).sort((a, b) => b.riders - a.riders);
}
